// Defaults are the Shopify embed. Other hosts opt in via ?embed_host= or a
// `solvpath_embed_config` postMessage.
import { POINTER_GUARD_EVENTS } from '@/constants'
import { isEmbedded } from './iframe'
import { isInteractivePreviewMode } from './previewMode'

export type EmbedHeightMode = 'content' | 'fill'

export interface EmbedConfig {
  host: string | null
  showHeader: boolean
  showFooter: boolean
  heightMode: EmbedHeightMode
  allowClick: boolean
  allowScroll: boolean
}

export const EMBED_CONFIG_MESSAGE = 'solvpath_embed_config'

const DEFAULTS: EmbedConfig = {
  host: null,
  showHeader: false,
  showFooter: false,
  heightMode: 'content',
  allowClick: true,
  allowScroll: true,
}

const listeners = new Set<(config: EmbedConfig) => void>()

function asBool(value: string | null, fallback: boolean): boolean {
  if (value == null) {
    return fallback
  }
  return value !== 'false' && value !== '0'
}

function readFromUrl(): Partial<EmbedConfig> {
  if (typeof window === 'undefined') {
    return {}
  }
  const q = new URLSearchParams(window.location.search)
  const host = q.get('embed_host')
  if (!host) {
    return {}
  }
  return {
    host,
    showHeader: asBool(q.get('embed_header'), true),
    showFooter: asBool(q.get('embed_footer'), true),
    heightMode: q.get('embed_height') === 'content' ? 'content' : 'fill',
    allowClick: asBool(q.get('embed_click'), true),
    allowScroll: asBool(q.get('embed_scroll'), true),
  }
}

function sanitize(input: any): Partial<EmbedConfig> {
  const next: Partial<EmbedConfig> = {}
  if (typeof input?.host === 'string') {
    next.host = input.host
  }
  for (const key of ['showHeader', 'showFooter', 'allowClick', 'allowScroll'] as const) {
    if (typeof input?.[key] === 'boolean') {
      next[key] = input[key]
    }
  }
  if (input?.heightMode === 'fill' || input?.heightMode === 'content') {
    next.heightMode = input.heightMode
  }
  return next
}

let current: EmbedConfig = { ...DEFAULTS, ...(isEmbedded() ? readFromUrl() : {}) }

// The one predicate for "Shopify iframe": embedded and we size the frame ourselves
// (heightMode 'content', the default). Host preview (?embed_host=) is 'fill' and the host
// sizes the frame. The editor frame (?preview=) is excluded explicitly. Do not use
// isEmbedded() for this, it is true for every mode. The CSS counterpart is
// `html.is-embedded:not(.embed-fill)`.
export function isShopifyEmbed(): boolean {
  return isEmbedded() && current.heightMode === 'content' && !isInteractivePreviewMode()
}

export function getEmbedConfig(): EmbedConfig {
  return current
}

export function subscribeEmbedConfig(listener: (config: EmbedConfig) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function applyDocumentClasses(): void {
  if (typeof document === 'undefined') {
    return
  }
  const root = document.documentElement.classList
  root.toggle('embed-fill', isEmbedded() && current.heightMode === 'fill')
  root.toggle('embed-no-scroll', isEmbedded() && !current.allowScroll)
}

function update(patch: Partial<EmbedConfig>): void {
  const next = { ...current, ...patch }
  if (Object.keys(next).every(key => (next as any)[key] === (current as any)[key])) {
    return
  }
  current = next
  applyDocumentClasses()
  listeners.forEach(listener => listener(current))
}

function setupClickBlocker(): () => void {
  const swallow = (event: Event) => {
    if (current.allowClick) {
      return
    }
    event.stopPropagation()
    event.preventDefault()
  }
  POINTER_GUARD_EVENTS.forEach(type => document.addEventListener(type, swallow, true))
  return () => POINTER_GUARD_EVENTS.forEach(type => document.removeEventListener(type, swallow, true))
}

export function setupEmbedConfig(): () => void {
  if (!isEmbedded()) {
    return () => {}
  }

  applyDocumentClasses()

  const onMessage = (event: MessageEvent) => {
    if (event.data?.type !== EMBED_CONFIG_MESSAGE) {
      return
    }
    update(sanitize(event.data))
  }
  window.addEventListener('message', onMessage)
  const teardownClicks = setupClickBlocker()

  return () => {
    window.removeEventListener('message', onMessage)
    teardownClicks()
  }
}
